import React, { useEffect, useRef, useState } from "react";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import {
  Button,
  Tabs,
  Tab,
  Box,
  createFilterOptions,
  Badge,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  AutocompleteOption,
  LinearProgress,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from "@mui/joy";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import BreadCrumbsV2 from "components/Breadcrumbs/BreadCrumbsV2";
import { RootState, useDispatch } from "store";
import { getClassNameByValue } from "utilities/UtilitiesFunctions";
import FeeCollectionReport from "./FeeCollectionReport";
import { fetchstudent } from "store/reducers/studentSlice";
import { useNavbar } from "context/NavbarContext";

// -----------------------------
// Interfaces / Types
// -----------------------------
interface CustomTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface StudentOption {
  id: string;
  name: string;
  admission: string;
  fatherName: string;
  profile: string;
  sId: string;
  dob: string;
  class: string;
}

// -----------------------------
// CustomTabPanel Component
// -----------------------------
function CustomTabPanel({
  children,
  value,
  index,
  ...other
}: CustomTabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// -----------------------------
// FeeManager Component
// -----------------------------
const FeeManager: React.FC = () => {
  const [value, setValue] = useState(0);
  const historyRef = useNavigate();
  const data = useSelector((state: RootState) => state.students.studentarray);
  const loading = useSelector((state: RootState) => state.students.loading);
  const dipatch = useDispatch();
  const [searchList, setSearchList] = useState<StudentOption[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const searchBoxRef = useRef<HTMLInputElement>(null);

  const { session } = useNavbar();

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const setFilteredData = (data: any[]) => {
    const filtered = data.map((item) => ({
      id: item.id,
      name: item.student_name,
      admission: item.admission_no,
      fatherName: item.father_name,
      profile: item.profil_url,
      sId: item.student_id,
      dob: item.dob,
      class: getClassNameByValue(item.class)?.toString() || "N/A",
    }));
    setSearchList(filtered);
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const filterOptions = createFilterOptions<StudentOption>({
    stringify: (option) =>
      option.name + option.sId + option.admission + option.fatherName,
  });

  const handleNextPageBtn = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDoc) {
      historyRef(`FeeDetails/${selectedDoc}`);
    } else {
      enqueueSnackbar("Error : Please enter student id or admission number !", {
        variant: "error",
      });
    }
  };
  const handleNextPageBtnNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDoc) {
      // const selectedData = data.filter((student) => student.id === selectedDoc);
      // historyRef(`FeeDetails/${selectedDoc}`, { state: selectedData });
      historyRef(`NewFeeDetails/${selectedDoc}`);
    } else {
      enqueueSnackbar("Error : Please enter student id or admission number !", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    searchBoxRef.current?.focus();
  }, []);

  useEffect(() => {
    if (data.length === 0) {
      dipatch(fetchstudent(session)); // Fetch students if not already fetched
    }
  }, []);

  return (
    <>
      <BreadCrumbsV2
        Icon={AccountBalanceWalletIcon}
        Path="Fee Management/Search Student"
      />
      <Box sx={{ width: "100%", mt: "16px" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={value} onChange={handleChange} aria-label="tabs">
            <Tab
              label="Student Fee Collection"
              {...a11yProps(0)}
              sx={{ textTransform: "capitalize" }}
            />
            <Tab
              label={
                <Badge
                  color="error"
                  badgeContent="New"
                  sx={{
                    "& .MuiBadge-badge": {
                      right: -11, // less negative so badge is more visible
                      top: 8,
                      zIndex: 1,
                    },
                  }}
                >
                  Payment Collectionㅤ
                </Badge>
              }
              {...a11yProps(1)}
              sx={{
                textTransform: "capitalize",
                pr: 4,
                minWidth: 180,
                paddingRight: 4,
              }} // increase minWidth and padding
            />
            <Tab
              label={
                <Badge
                  color="error"
                  badgeContent="New"
                  sx={{
                    "& .MuiBadge-badge": {
                      right: -20, // less negative so badge is more visible
                      top: 8,
                      zIndex: 1,
                    },
                  }}
                >
                  Collection Report
                </Badge>
              }
              {...a11yProps(2)}
              sx={{
                textTransform: "capitalize",
                pr: 4,
                minWidth: 180,
                paddingRight: 6,
              }} // increase minWidth and padding
            />
          </Tabs>
        </Box>

        {loading && <LinearProgress />}

        <CustomTabPanel value={value} index={0}>
          <Box
            component="form"
            onSubmit={handleNextPageBtn}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "30vh",
            }}
          >
            <Autocomplete
              id="student-search"
              color="primary"
              placeholder="Search with Student ID/Admission No"
              options={searchList}
              autoHighlight
              filterOptions={filterOptions}
              sx={{ width: 450, m: 1 }}
              getOptionLabel={(option) => `${option.name} - ${option.id}`}
              onChange={(_e, val) => setSelectedDoc(val?.id ?? null)}
              renderOption={(props, option) => (
                <AutocompleteOption {...props}>
                  <ListItemDecorator>
                    <img
                      loading="lazy"
                      width="20"
                      src={option.profile}
                      alt=""
                    />
                  </ListItemDecorator>
                  <ListItemContent
                    sx={{
                      fontSize: "sm",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <b>
                      {option.name} | {option.class}
                    </b>
                    <Typography level="body-xs" fontSize={"14px"}>
                      {option.admission} | {option.fatherName} | {option.dob}
                    </Typography>
                  </ListItemContent>
                </AutocompleteOption>
              )}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ height: 36 }}
              disableElevation
            >
              Search
            </Button>
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <Box
            component="form"
            onSubmit={handleNextPageBtnNew}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "30vh",
            }}
          >
            <Autocomplete
              id="student-search"
              color="primary"
              placeholder="Search with Student ID/Admission No"
              options={searchList}
              autoHighlight
              filterOptions={filterOptions}
              sx={{ width: 450, m: 1 }}
              getOptionLabel={(option) => `${option.name} - ${option.id}`}
              onChange={(_e, val) => setSelectedDoc(val?.id ?? null)}
              renderOption={(props, option) => (
                <AutocompleteOption {...props}>
                  <ListItemDecorator>
                    <img
                      loading="lazy"
                      width="20"
                      src={option.profile}
                      alt=""
                    />
                  </ListItemDecorator>
                  <ListItemContent
                    sx={{
                      fontSize: "sm",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <b>
                      {option.name} | {option.class}
                    </b>
                    <Typography level="body-xs" fontSize={"14px"}>
                      {option.admission} | {option.fatherName} | {option.dob}
                    </Typography>
                  </ListItemContent>
                </AutocompleteOption>
              )}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ height: 36 }}
              disableElevation
            >
              Search
            </Button>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <FeeCollectionReport />
        </CustomTabPanel>
      </Box>
    </>
  );
};

export default FeeManager;
